import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Map, { Layer, Source, type MapRef } from 'react-map-gl';
import type { FillLayer, LineLayer } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Layers, Satellite } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { MAPBOX_TOKEN } from '../utilities/mapbox';
import { geometryBounds } from '../heatmap/geometryBounds';
import { PhaseParcelRow } from './phaseParcelsApi';
import { isOnPlot } from './parcelPlot';
import { ParcelHoverSource } from './parcelHover';
import ParcelTooltip, { useParcelHover } from './ParcelTooltip';

/**
 * The plot drawn as what it is: a set of parcels, in three states.
 *
 * A table of Flurstücksnummern answers "which" and not "where", and where is the question — whether
 * the yard behind the building belongs to it is settled by looking at the yard, not by reading
 * "3124/2". So the same rows the table listed are drawn, and the ones around them are drawn faintly
 * beside them, because a plot is only ever decided against its neighbours.
 *
 * Three fills and no legend needed to tell them apart at a glance: solid for what belongs to the
 * plot, dashed for what the estimator has not settled, and a bare outline for the neighbours. Where
 * `onToggle` is given, all three are clickable and the click means the obvious thing — take this one
 * off the plot, or put it on.
 */

const COLOR = {
    certain: '#C0392B',
    open: '#D08A17',
    nearby: '#7A8798',
};

interface Properties {
    rows: PhaseParcelRow[];
    /**
     * Parcels of the neighbourhood that are not on the plot. Absent where nothing may be added.
     *
     * They carry their cadastral data too, not only an outline: the pointer asks the same question
     * of a neighbour as of a parcel on the plot, and "which Flurstück is that" is most of the reason
     * one hovers over a neighbour at all.
     */
    nearby?: Array<{ parcelId: string; geometry: any } & ParcelHoverSource>;
    /** The building's outline, drawn on top so the plot can be read against it. */
    footprint?: any;
    height: number;
    /** Absent makes the map a picture: no clicks, no cursor change. */
    onToggle?: (parcelId: string, onPlot: boolean) => void;
    /** Written while a decision is in flight, so a second click on the same parcel is ignored. */
    busyParcelId?: string | null;
}

const collection = (features: any[]) => ({ type: 'FeatureCollection' as const, features });

const featuresOf = (parcels: Array<{ parcelId: string; geometry: any }>) =>
    parcels
        .filter((parcel) => parcel.geometry)
        .map((parcel) => ({
            type: 'Feature' as const,
            properties: { parcelId: parcel.parcelId },
            geometry: parcel.geometry,
        }));

/**
 * The frame: everything on the plot, plus a margin.
 *
 * Framed on the plot rather than on everything drawn — the neighbours are context and a wide
 * neighbourhood would push the plot itself down to a few pixels. The margin is what makes the
 * neighbours reachable at all: without it the plot fills the frame and there is nothing to add.
 */
const boundsOf = (rows: PhaseParcelRow[], footprint: any): [[number, number], [number, number]] | null => {
    const boxes = [...rows.map((row) => row.geometry), footprint]
        .map(geometryBounds)
        .filter(Boolean) as Array<[[number, number], [number, number]]>;

    if (boxes.length === 0) return null;

    const minLon = Math.min(...boxes.map((box) => box[0][0]));
    const minLat = Math.min(...boxes.map((box) => box[0][1]));
    const maxLon = Math.max(...boxes.map((box) => box[1][0]));
    const maxLat = Math.max(...boxes.map((box) => box[1][1]));

    return [
        [minLon, minLat],
        [maxLon, maxLat],
    ];
};

export default function ParcelMap({ rows, nearby, footprint, height, onToggle, busyParcelId }: Properties) {
    const { t } = useTranslation();
    const [satellite, setSatellite] = useState(false);
    const [mapLoaded, setMapLoaded] = useState(false);
    const mapRef = useRef<MapRef | null>(null);
    const { hovered, onMouseMove, onMouseLeave } = useParcelHover();

    const certain = useMemo(() => collection(featuresOf(rows.filter((row) => isOnPlot(row.role)))), [rows]);
    const open = useMemo(() => collection(featuresOf(rows.filter((row) => row.role === 'open'))), [rows]);

    // A neighbour that is already on the plot is drawn as part of it; drawing both would put a grey
    // outline over a red one and make the plot look unsettled where it is not.
    // Only what is actually drawn counts as known. A row that is neither on the plot nor open —
    // one the estimator rejected — is not on the map at all, and hiding its neighbour outline too
    // would leave a hole nobody could click.
    const known = useMemo(
        () => rows.filter((row) => isOnPlot(row.role) || row.role === 'open').map((row) => row.parcelId),
        [rows],
    );
    const others = useMemo(
        () => collection(featuresOf((nearby ?? []).filter((parcel) => !known.includes(parcel.parcelId)))),
        [nearby, known],
    );

    const shape = useMemo(() => (footprint ? collection(featuresOf([{ parcelId: '', geometry: footprint }])) : null), [
        footprint,
    ]);

    const bounds = useMemo(() => boundsOf(rows, footprint), [rows, footprint]);

    // The camera follows the plot rather than being set once: a parcel added at the edge belongs in
    // the frame, and the reader should not have to go looking for what they just clicked.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !bounds) return;

        map.fitBounds(bounds, { padding: 60, duration: 0, maxZoom: 18 });
    }, [bounds, mapLoaded]);

    const roleOf = useCallback(
        (parcelId: string) => rows.find((row) => row.parcelId === parcelId)?.role,
        [rows],
    );

    const handleClick = useCallback(
        (event: any) => {
            if (!onToggle) return;

            const parcelId = event?.features?.[0]?.properties?.parcelId;
            if (!parcelId || parcelId === busyParcelId) return;

            onToggle(String(parcelId), isOnPlot(roleOf(String(parcelId))));
        },
        [onToggle, roleOf, busyParcelId],
    );

    // Always interactive, whether or not the map can be clicked: the pointer has something to say
    // about every parcel, and a read-only map is exactly where a reader most needs telling what
    // they are looking at.
    const interactive = ['parcelsCertainFill', 'parcelsOpenFill', 'parcelsNearbyFill'];

    /** What is known about the hovered parcel — from the plot if it is on it, else the cadastre. */
    const hoveredParcel = hovered
        ? rows.find((row) => row.parcelId === hovered.parcelId) ??
          (nearby ?? []).find((parcel) => parcel.parcelId === hovered.parcelId) ??
          null
        : null;

    return (
        <div className="relative overflow-hidden rounded-lg border">
            <Button
                type="button"
                variant="secondary"
                size="sm"
                className="absolute right-2 top-2 z-[2] gap-1.5 rounded-full shadow-sm"
                onClick={() => setSatellite(!satellite)}
            >
                {satellite ? <Layers size={14} /> : <Satellite size={14} />}
                {t(satellite ? 'portfolioDashboard.map.showMap' : 'portfolioDashboard.map.showSatellite')}
            </Button>

            <Map
                ref={mapRef}
                mapboxAccessToken={MAPBOX_TOKEN}
                initialViewState={{ longitude: 8.669347, latitude: 50.106277, zoom: 15 }}
                style={{ width: '100%', height }}
                mapStyle={
                    satellite ? 'mapbox://styles/mapbox/satellite-streets-v9' : 'mapbox://styles/mapbox/streets-v9'
                }
                interactiveLayerIds={interactive}
                cursor={onToggle ? 'pointer' : 'grab'}
                onClick={handleClick}
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseLeave}
                onLoad={() => setMapLoaded(true)}
            >
                <ParcelTooltip hovered={hovered} parcel={hoveredParcel} />

                {mapLoaded ? (
                    <>
                        {/* Neighbours first and faintest — they are the ground the plot is read
                            against, and anything drawn over them belongs to the answer. */}
                        <Source id="parcelsNearby" type="geojson" data={others as any}>
                            <Layer
                                {...({
                                    id: 'parcelsNearbyFill',
                                    type: 'fill',
                                    // Not invisible: a fill of zero still answers a click, but a
                                    // faint one also tells the reader there is something to click.
                                    paint: { 'fill-color': COLOR.nearby, 'fill-opacity': 0.06 },
                                } as FillLayer)}
                            />
                            <Layer
                                {...({
                                    id: 'parcelsNearbyLine',
                                    type: 'line',
                                    paint: { 'line-color': COLOR.nearby, 'line-width': 1, 'line-opacity': 0.7 },
                                } as LineLayer)}
                            />
                        </Source>

                        <Source id="parcelsOpen" type="geojson" data={open as any}>
                            <Layer
                                {...({
                                    id: 'parcelsOpenFill',
                                    type: 'fill',
                                    paint: { 'fill-color': COLOR.open, 'fill-opacity': 0.14 },
                                } as FillLayer)}
                            />
                            <Layer
                                {...({
                                    id: 'parcelsOpenLine',
                                    type: 'line',
                                    // Dashed for undecided: the outline itself is the doubt.
                                    paint: { 'line-color': COLOR.open, 'line-width': 1.6, 'line-dasharray': [2, 1.5] },
                                } as LineLayer)}
                            />
                        </Source>

                        <Source id="parcelsCertain" type="geojson" data={certain as any}>
                            <Layer
                                {...({
                                    id: 'parcelsCertainFill',
                                    type: 'fill',
                                    paint: { 'fill-color': COLOR.certain, 'fill-opacity': 0.22 },
                                } as FillLayer)}
                            />
                            <Layer
                                {...({
                                    id: 'parcelsCertainLine',
                                    type: 'line',
                                    paint: { 'line-color': COLOR.certain, 'line-width': 2 },
                                } as LineLayer)}
                            />
                        </Source>

                        {shape ? (
                            <Source id="parcelsFootprint" type="geojson" data={shape as any}>
                                <Layer
                                    {...({
                                        id: 'parcelsFootprintFill',
                                        type: 'fill',
                                        paint: { 'fill-color': '#1F2933', 'fill-opacity': 0.45 },
                                    } as FillLayer)}
                                />
                            </Source>
                        ) : (
                            <></>
                        )}
                    </>
                ) : (
                    <></>
                )}
            </Map>
        </div>
    );
}
